/**
 * useDealProgressStream — Phase 4 SSE replacement for the polling
 * useDealProgress hook. Drop-in compatible with the existing
 * DealProgressTimeline consumer.
 *
 * Source: the Phase 4 deal-progress-stream Lambda (Function URL with
 * `awslambda.streamifyResponse`). The Lambda holds a Postgres LISTEN
 * connection on `deal_progress_<dealAnalysisId>` and forwards every
 * stage transition as an SSE `message` frame. On reconnect it queries
 * `DealAnalysis.progressEvents` (JSONB) for events newer than the
 * client's `Last-Event-ID` and replays them — so missed events never
 * disappear, regardless of how long the user's tab was backgrounded.
 *
 * Auth: Clerk session token via query string (browser EventSource API
 * can't set custom headers). Token is short-lived; native EventSource
 * auto-reconnect re-mints it via `window.Clerk.session.getToken()`.
 *
 * Status derivation: the SSE Lambda doesn't ship a top-level `status`
 * field — we derive it from the events stream:
 *   - no events yet, connection open  → 'pending'
 *   - any event with status='failed'  → 'failed'
 *   - last stage 'RENDER_CARDS' done  → 'complete'
 *   - otherwise (events present)      → 'running'
 *   - no connection                   → 'idle'
 *
 * Once status flips to 'complete' or 'failed' the EventSource closes
 * itself — no need to keep the channel open after the engine is done.
 */

import { useEffect, useRef, useState } from "react";
import type { DealProgressEvent } from "src/api/deal-engine/DealEngine";
import { API_BASE_URL } from "src/constants/api.constants";

export interface UseDealProgressStreamResult {
  events: DealProgressEvent[];
  /** 'pending' | 'running' | 'complete' | 'failed' | 'unknown' | 'idle' */
  status: string;
  latestStage: DealProgressEvent | null;
  /** True while the SSE connection is open (mirrors the polling hook's field). */
  polling: boolean;
}

const TERMINAL_STAGE = "RENDER_CARDS";

function deriveStatus(events: DealProgressEvent[], connected: boolean): string {
  if (events.length === 0) return connected ? "pending" : "idle";
  // Only a TERMINAL failure aborts the analysis. A swallowed enrichment
  // failure (e.g. THREAD_INSIGHTS "skipped") emits status:"failed" WITHOUT
  // terminal:true and the pipeline keeps going — so it must not flip the
  // whole header to "Failed". The engine sets terminal:true on both the
  // successful RENDER_CARDS event and a fatal abort; key on that flag.
  if (events.some((e) => e.status === "failed" && e.terminal === true)) return "failed";
  if (events.some((e) => e.stage === TERMINAL_STAGE && e.status === "completed")) {
    return "complete";
  }
  return "running";
}

// Map the backend's latestAnalysis.status snapshot (`/deals/get` response)
// onto the SSE hook's status vocabulary so the strip header can render the
// correct pill on the first paint instead of waiting ~15s for the SSE
// replay to land.
function normalizeInitialStatus(s: string | undefined): string {
  if (!s) return "idle";
  const lower = s.toLowerCase();
  if (lower === "complete" || lower === "completed" || lower === "success") return "complete";
  if (lower === "failed" || lower === "error") return "failed";
  if (lower === "running" || lower === "in_progress" || lower === "processing") return "running";
  if (lower === "pending" || lower === "queued") return "pending";
  return "idle";
}

export function useDealProgressStream(
  dealAnalysisId: string | null,
  initialStatus?: string,
): UseDealProgressStreamResult {
  const seededStatus = normalizeInitialStatus(initialStatus);
  const [events, setEvents] = useState<DealProgressEvent[]>([]);
  const [status, setStatus] = useState<string>(seededStatus);
  const [polling, setPolling] = useState<boolean>(false);

  // useState's initial value only fires on first render. When the
  // snapshot status arrives after the first render (e.g. /getContract
  // resolves a tick later), the seed is otherwise ignored. React to
  // initialStatus changes so a known-terminal snapshot still wins,
  // but don't clobber a live SSE-derived status — running/complete
  // from the stream are more authoritative than the snapshot.
  useEffect(() => {
    if (!initialStatus) return;
    const normalized = normalizeInitialStatus(initialStatus);
    if (normalized !== 'complete' && normalized !== 'failed') return;
    setStatus((prev) =>
      prev === 'idle' || prev === 'pending' ? normalized : prev,
    );
  }, [initialStatus]);
  // Hold the EventSource in a ref so React StrictMode's double-mount
  // doesn't end up with two parallel connections.
  const esRef = useRef<EventSource | null>(null);
  // Track the set of event `at` timestamps we've already merged so the
  // replay-on-reconnect path doesn't double-render stages the user
  // already saw.
  const seenAtRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!dealAnalysisId) {
      setEvents([]);
      setStatus("idle");
      setPolling(false);
      seenAtRef.current = new Set();
      return;
    }

    // API_BASE_URL resolves from environments.json[tag].apiEndpoint.
    // Strip trailing slashes — `new URL("//sse/...", origin)` parses
    // leading `//` as protocol-relative and EventSource dies in
    // readyState=0 forever (#254).
    const apiBase = API_BASE_URL.replace(/\/+$/, "");
    const SSE_URL =
      import.meta.env.VITE_DEAL_PROGRESS_STREAM_URL ||
      `${apiBase}/sse/deal-progress`;

    let cancelled = false;
    let es: EventSource | null = null;
    seenAtRef.current = new Set();
    setEvents([]);
    // Don't clobber a known-terminal seed from the /deals/get snapshot —
    // if the backend already says complete/failed, the SSE reconnect will
    // replay events and overwrite naturally; the pill should not regress
    // to "pending" in the meantime.
    setStatus((prev) =>
      prev === "complete" || prev === "failed" ? prev : "pending",
    );
    setPolling(true);

    async function connect() {
      const clerk =
        typeof window !== "undefined" ? (window as any).Clerk : null;
      const token: string | undefined = await clerk?.session?.getToken?.();
      if (!token) {
        console.warn(
          "[useDealProgressStream] no Clerk token available — skipping connect",
        );
        setStatus("unknown");
        setPolling(false);
        return;
      }
      if (cancelled) return;

      const url = `${SSE_URL}?dealAnalysisId=${encodeURIComponent(
        dealAnalysisId!,
      )}&token=${encodeURIComponent(token)}`;
      es = new EventSource(url);
      esRef.current = es;

      es.addEventListener("open", () => {
        if (cancelled) return;
        // Connection is up; status stays 'pending' until the first event
        // lands or 'open' frame arrives below.
      });

      es.addEventListener(
        "open" as any,
        (_ev: MessageEvent<string>) => {
          // Server-emitted 'open' frame — payload is { dealAnalysisId,
          // channel } but we just need to confirm the LISTEN is live.
          // No state change beyond what 'message' events drive.
        },
      );

      es.addEventListener("message", (ev: MessageEvent<string>) => {
        if (cancelled) return;
        let payload: DealProgressEvent;
        try {
          payload = JSON.parse(ev.data) as DealProgressEvent;
        } catch (err) {
          console.warn(
            "[useDealProgressStream] non-JSON SSE payload:",
            ev.data,
          );
          return;
        }

        // Dedupe by `at` so replay-on-reconnect doesn't double-add events
        // the client already merged before the disconnect.
        if (seenAtRef.current.has(payload.at)) return;
        seenAtRef.current.add(payload.at);

        setEvents((prev) => {
          const next = [...prev, payload];
          const newStatus = deriveStatus(next, true);
          setStatus(newStatus);
          // Engine's done — close the SSE so we don't sit on an idle
          // Lambda invocation forever.
          if (newStatus === "complete" || newStatus === "failed") {
            es?.close();
            esRef.current = null;
            setPolling(false);
          }
          return next;
        });
      });

      es.addEventListener("error", (_ev) => {
        if (cancelled) return;
        // EventSource handles its own reconnect (every 5s per server's
        // retry: directive). We only flip polling=false on a true close
        // (auth failure → 4xx → permanent close).
        if (es?.readyState === EventSource.CLOSED) {
          setPolling(false);
          // Functional setState — `status` from closure is captured at
          // effect-mount time and reads "idle" forever, even after a
          // successful "complete" 1ms earlier. Without this the strip
          // header gets stuck on "Idle" right after the engine finishes
          // because the deliberate close-on-complete fires this handler
          // and overwrites the just-set "complete" with "unknown".
          setStatus((prev) =>
            prev === "complete" || prev === "failed" ? prev : "unknown",
          );
        }
      });
    }

    void connect();

    return () => {
      cancelled = true;
      if (es) {
        es.close();
        es = null;
      }
      esRef.current = null;
      setPolling(false);
    };
  }, [dealAnalysisId]);

  const latestStage = events.length > 0 ? events[events.length - 1] : null;

  return { events, status, latestStage, polling };
}
